import { Style } from 'mapbox-gl';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import shortid from 'shortid';

import IdentifyPopup from '../components/IdentifyPopup';
import MapComponent from '../components/Map';
import Agreement from '../models/Agreement';
import { LayerBounds } from '../models/Layer';
import { PopupState } from '../models/PopupState';
import {
  clearIdentify,
  clearSelectedIdentifyFeature,
  newIdentify,
  requestDocuments,
  selectIdentifyFeature,
  setAgreementsPopupTab,
  setMapLayerBounds,
  setMapStyleBaseByName
} from '../store/actions';
import { State } from '../store/reducer';
import {
  getAgreementsPopupTab,
  getBasemapStyleNames,
  getIdentifyPopupWithAgreement,
  getIsAgreementsTableOpen,
  getIsPopupLoading,
  getMapLocation,
  getMapPopupLayers,
  getMapStyle,
  getSelectedAgreement,
  getSelectedTableAgreementMapLocation
} from '../store/selectors';

export interface StateProps {
  baseMapStyles: string[];
  center: [number, number];
  isAgreementsTableOpen: boolean;
  mapStyle: Style;
  isPopupLoading: boolean;
  popupLayers: string[];
  popupState: PopupState | null;
  selectedAgreement: Agreement | null;
  selectedTableAgreementMapLocation: {
    popupAnchor: [number, number] | null;
    bounds: [number, number, number, number] | null;
  };
  zoom: number;
  agreementsTabIndex: number;
}

export interface DispatchProps {
  onAgreementsPopupTabChange: (index: number) => void;
  onAgreementBounds: (agreementBounds: LayerBounds) => void;
  onFeatureSelect: (id: string) => void;
  onIdentify: (identify: {
    location: { lng: number; lat: number };
    features: Array<{ id: string; [key: string]: any }>;
  }) => void;
  onPopupBack: () => void;
  onPopupClose: () => void;
  onRequestDocuments: (agreementId: string) => void;
  onSwitchBaseMap: (id: string) => void;
}

const Map: React.FC<StateProps & DispatchProps> = ({
  agreementsTabIndex,
  baseMapStyles,
  center,
  isAgreementsTableOpen,
  isPopupLoading,
  mapStyle,
  onAgreementsPopupTabChange,
  onAgreementBounds,
  onFeatureSelect,
  onIdentify,
  onPopupBack,
  onPopupClose,
  onRequestDocuments,
  onSwitchBaseMap,
  popupLayers,
  popupState,
  selectedTableAgreementMapLocation,
  selectedAgreement,
  zoom
}) => {
  const [triggerMapResize, setTriggerMapResize] = useState('');

  useEffect(() => {
    setTriggerMapResize(shortid.generate());
  }, [isAgreementsTableOpen]);
  return (
    <MapComponent
      mapStyle={mapStyle}
      baseMapStyles={baseMapStyles}
      onSwitchBaseMap={onSwitchBaseMap}
      center={center}
      triggerMapResize={triggerMapResize}
      zoom={zoom}
      fitBounds={selectedTableAgreementMapLocation.bounds}
      identifyLayers={popupLayers}
      isPopupLoading={isPopupLoading}
      popupComponent={
        <IdentifyPopup
          isPopupLoading={isPopupLoading}
          popupState={popupState}
          onFeatureSelect={onFeatureSelect}
          onPopupBack={onPopupBack}
          onPopupClose={onPopupClose}
          onRequestDocuments={onRequestDocuments}
          agreementsTabIndex={agreementsTabIndex}
          onAgreementsPopupTabChange={onAgreementsPopupTabChange}
          selectedAgreement={selectedAgreement}
          selectedTableAgreementMapLocation={selectedTableAgreementMapLocation}
        />
      }
      onAgreementBounds={onAgreementBounds}
      onIdentify={onIdentify}
    />
  );
};

const mapDispatch = {
  onAgreementsPopupTabChange: setAgreementsPopupTab,
  onAgreementBounds: setMapLayerBounds,
  onFeatureSelect: selectIdentifyFeature,
  onIdentify: newIdentify,
  onPopupBack: clearSelectedIdentifyFeature,
  onPopupClose: clearIdentify,
  onRequestDocuments: requestDocuments,
  onSwitchBaseMap: setMapStyleBaseByName
};

export const mapState = (state: State): StateProps => {
  const { center, zoom } = getMapLocation(state);

  return {
    agreementsTabIndex: getAgreementsPopupTab(state),
    baseMapStyles: getBasemapStyleNames(state),
    center,
    isAgreementsTableOpen: getIsAgreementsTableOpen(state),
    isPopupLoading: getIsPopupLoading(state),
    mapStyle: getMapStyle(state),
    popupLayers: getMapPopupLayers(state),
    popupState: getIdentifyPopupWithAgreement(state),
    selectedTableAgreementMapLocation: getSelectedTableAgreementMapLocation(state),
    selectedAgreement: getSelectedAgreement(state),
    zoom
  };
};

export default connect<StateProps, DispatchProps, {}, State>(
  mapState,
  mapDispatch
)(Map);
